<script setup lang="ts">
import { inject, ref } from 'vue';
import {
  AdpDocument,
  AdpDocumentOptionAction,
  AdpDocumentOptionActionName,
} from '@/types';
import { visibleAdpCardKey } from '@/utils/injectionKeys';
import AdpDocumentCommentButton from './adp-document-comment-button.vue';
import AdpDocumentGroupMarkButton from './adp-document-group-mark-button.vue';
import AdpDocumentMarkButton from './adp-document-mark-button.vue';
import AdpDocumentOptionMenusButton from './adp-document-option-menus-button.vue';

const props = withDefaults(
  defineProps<{
    adpDocument: AdpDocument;
    isMarked: boolean;
    markedUsers: {
      id: number;
      name: string;
    }[];
    markedGroups: {
      id: number;
      name: string;
    }[];
    commentedUsers: {
      id: number;
      name: string;
      commentId: number;
    }[];
    optionActions: AdpDocumentOptionAction[];
    markHandler?: () => unknown | Promise<unknown>;
    disabled?: boolean;
    isNarrow?: boolean;
    size?: 'm' | 's';
    zindex?: number;
    showOptionMenu?: boolean;
  }>(),
  {
    markHandler: undefined,
    disabled: false,
    isNarrow: false,
    size: 'm',
    zindex: 2,
    showOptionMenu: true,
  },
);

const emit = defineEmits<{
  handlePopupMenuClick: [];
  handleGroupMarkClick: [];
  handleCommentClick: [];
  optionAction: [emitName: AdpDocumentOptionActionName];
}>();

const isMarking = ref(false);
const handleMarkClick = async () => {
  if (isMarking.value) return;
  isMarking.value = true;
  try {
    await props.markHandler?.();
  } finally {
    isMarking.value = false;
  }
};
const handlePopupMenuClick = () => {
  emit('handlePopupMenuClick');
};

const handleGroupMarkClick = () => {
  emit('handleGroupMarkClick');
};

const handleCommentClick = () => {
  emit('handleCommentClick');
};

// 親が表示監視している場合のみ可視判定が渡ってくる。それ以外は常に表示する。
const visibleAdpCard = inject(visibleAdpCardKey, ref(true));
</script>

<template>
  <div
    class="adp-document-action-bar"
    :class="{ 'is-narrow': isNarrow, small: size === 's' }"
  >
    <div class="base-menu-area">
      <span v-if="visibleAdpCard">
        <AdpDocumentMarkButton
          :is-marked="isMarked"
          :marked-users="markedUsers"
          :disabled="disabled"
          :is-marking="isMarking"
          :small="size === 's'"
          @handle-mark-click="handleMarkClick"
          @handle-popup-menu-click="handlePopupMenuClick"
        />
      </span>
      <AdpDocumentGroupMarkButton
        :marked-groups="markedGroups"
        :disabled="disabled"
        :size="size"
        @click="handleGroupMarkClick"
      />
      <AdpDocumentCommentButton
        :adp-document="adpDocument"
        :commented-users="commentedUsers"
        :disabled="disabled"
        :size="size"
        @click="handleCommentClick"
      />
    </div>
    <div class="option-menu-area" v-if="visibleAdpCard && showOptionMenu">
      <AdpDocumentOptionMenusButton
        :option-actions="optionActions"
        :disabled="disabled"
        :size="size"
        :zindex="zindex"
        @option-action="emit('optionAction', $event)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.adp-document-action-bar {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  &.is-narrow {
    justify-content: space-between;
  }

  &.small {
    gap: 16px;

    .base-menu-area {
      gap: 8px;
    }
  }
}

.base-menu-area {
  display: flex;
  align-items: center;
}

.option-menu-area {
  display: flex;
  align-items: center;
}
</style>
